import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, GraduationCap, ChevronDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getSource } from "@/lib/source";

const EMPTY = {
  student_name: "", parent_name: "", mobile: "", whatsapp: "", email: "",
  current_standard: "", school_name: "", course_interested: "", preferred_batch: "",
  message: "", consent: false, website: "",
};

const COURSES = ["Nursery to Std 5", "Std 6 to Std 8", "Std 9", "Std 10 (SSC)", "Commerce - Std 11", "Commerce - Std 12", "Degree - Accounting", "Other"];

export default function EnquiryModal({ open, onOpenChange, prefill = {} }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);

  useEffect(() => {
    if (open) { setForm({ ...EMPTY, ...prefill }); setDone(false); setError(""); setMore(false); }
  }, [open, prefill]);

  const set = (k, v) => { setError(""); setForm((f) => ({ ...f, [k]: v })); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy.current) return;
    let msg = "";
    if (!form.parent_name.trim() || !form.mobile.trim() || !form.course_interested) msg = "Please fill Parent name, Mobile number and Course/Class.";
    else if (form.mobile.replace(/\D/g, "").length < 10) msg = "Please enter a valid 10-digit mobile number.";
    else if (!form.consent) msg = "Please tick the consent box so PTG can contact you.";
    if (msg) { setError(msg); toast.error(msg); return; }
    busy.current = true;
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, source: getSource() });
      setDone(true);
      toast.success("Enquiry submitted successfully!");
    } catch (err) {
      const d = err.response?.data?.detail;
      const m = typeof d === "string" ? d : "Something went wrong. Please try again, or call/WhatsApp us.";
      setError(m); toast.error(m);
    } finally {
      busy.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="enquiry-modal">
        {done ? (
          <div className="py-10 text-center" data-testid="enquiry-success">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Thank you for contacting PTG.</h3>
            <p className="mt-2 text-slate-600">Our team will contact you shortly.</p>
            <Button className="mt-6 bg-blue-900 hover:bg-blue-950" onClick={() => onOpenChange(false)} data-testid="enquiry-success-close">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl">
                <GraduationCap className="h-6 w-6 text-blue-900" /> Admission Enquiry
              </DialogTitle>
              <DialogDescription>Just 3 quick details. Our team will call you back.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="enquiry-form">
              {/* Honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="hidden" aria-hidden="true" />

              <Field label="Parent Name *"><Input data-testid="enquiry-parent-name" value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} placeholder="Parent's full name" /></Field>
              <Field label="Mobile Number *"><Input data-testid="enquiry-mobile" type="tel" inputMode="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" /></Field>
              <div className="sm:col-span-2"><Field label="Course/Class Interested In *">
                <Select value={form.course_interested} onValueChange={(v) => set("course_interested", v)}>
                  <SelectTrigger data-testid="enquiry-course"><SelectValue placeholder="Select a course" /></SelectTrigger>
                  <SelectContent>
                    {form.course_interested && !COURSES.includes(form.course_interested) && <SelectItem value={form.course_interested}>{form.course_interested}</SelectItem>}
                    {COURSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field></div>
              <button type="button" onClick={() => setMore(!more)} className="sm:col-span-2 flex items-center gap-1 text-sm font-semibold text-blue-900 hover:text-blue-700" data-testid="enquiry-more-toggle">
                <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Hide extra details" : "Add more details (optional)"}
              </button>
              {more && <>
              <Field label="Student Name"><Input data-testid="enquiry-student-name" value={form.student_name} onChange={(e) => set("student_name", e.target.value)} placeholder="Student's full name" /></Field>
              <Field label="WhatsApp Number"><Input data-testid="enquiry-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="WhatsApp number" /></Field>
              <Field label="Email"><Input data-testid="enquiry-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@example.com" /></Field>
              <Field label="Current Standard/Class"><Input data-testid="enquiry-current-standard" value={form.current_standard} onChange={(e) => set("current_standard", e.target.value)} placeholder="e.g. Std 9" /></Field>
              <Field label="School Name"><Input data-testid="enquiry-school" value={form.school_name} onChange={(e) => set("school_name", e.target.value)} placeholder="School name" /></Field>
              <Field label="Preferred Batch/Timing"><Input data-testid="enquiry-batch" value={form.preferred_batch} onChange={(e) => set("preferred_batch", e.target.value)} placeholder="e.g. Morning / Evening" /></Field>
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Message / Additional Information</Label>
                <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything you'd like us to know" rows={3} />
              </div>
              </>}
              <div className="sm:col-span-2 flex items-start gap-3 rounded-lg bg-sky-50 p-3">
                <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="enquiry-consent" className="mt-0.5" />
                <Label htmlFor="consent" className="text-sm leading-snug text-slate-600">
                  I consent to being contacted by PTG regarding this admission enquiry. *
                </Label>
              </div>
              {error && <div className="sm:col-span-2 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert" data-testid="enquiry-error"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}</div>}
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-950 h-11 text-base" data-testid="enquiry-submit">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : "Submit Enquiry"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</Label>
    {children}
  </div>
);
